import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form } from '../../../services/formService';
import { Card, Button, Badge, Spinner, Table, Alert, Form as UIForm } from '@/components/ui';
import { taskService } from '../../../services/taskService';
import { activityService } from '../../../services/activityService';
import { employeeService } from '../../../services/employeeService';
import type { Task } from '../../../types/Task';
import type { Activity } from '../../../types/Activity';
import type { Employee } from '../../../types/Employee';
import EditableField from './EditableField';
import VehicleUnitEditableField, { isVehicleUnitFieldName } from './VehicleUnitEditableField';
import { SearchableDropdown } from '../../../components/SearchableDropdown';
import FormHistoryModal from './FormHistoryModal';
import { usePermissions } from '../../../hooks/usePermissions';
import FormHeader from './FormViews/@components/FormHeader';
import SectionCard from './FormViews/@components/SectionCard';
import FieldTable from './FormViews/@components/FieldTable';
import { groupFieldsByTask } from './FormViews/@components/TaskGrouper';
import ServerPagination from '../../../components/ServerPagination';
import Toast, { ToastType } from '../../../components/Toast';
import FormPictures from './FormPictures';
import {
  validateForm,
  FormValidationResult,
  requiresCHRISReporting,
  TASK_ZERO_LABOUR_HOURS_CODE,
} from '../utils/formValidation';
import { getDateTimeLocalValue, formatDateTimeLocalForSave } from '../utils/formDateTimeUtils';
import { getFormEmployeeDisplayName } from '../utils/formDisplayName';
import {
  formatMechanicHoursWorkedFromGroupFields,
  formatMechanicMileageDisplay,
  formatStoredDurationAnswerForDisplay,
  formatMechanicActivityNumberTimber,
  getMechanicActivityFieldsForGroup,
  getMechanicActivityTabTitle,
  getMechanicTaskFieldsForGroup,
  getMechanicMileageForWorkCompletedGroup,
  getMechanicVehicleUnitForWorkCompletedGroup,
  getMechanicVehicleUsageFieldsForDisplay,
  getMechanicWorkCompletedRows,
  groupMechanicFieldsByWorkIndex,
  buildMechanicSubmissionLabourLineKey,
  isMechanicDailyLog,
  isMechanicDefectMarkedCompleted,
  isMechanicGroupVehicleWorkAndRepairs,
  sortMechanicGroupKeys,
  type MechanicActivityFields
} from '../utils/mechanicDailyLog';
import type { FormLabourOvertimeAllocationRow } from '../../../utils/labourOvertimeDisplay';
import { canEditDailyLogForForm, canManageDailyLogForForm } from '../utils/submissionPermissions';
import { HIRED_EQUIPMENT_RESERVED_TICKET_FIELD } from '../../hired-equipment/types';

interface FormCompleteViewProps {
  form: Form;
  isExpanded?: boolean;
  onExpandToggle: (formId: string, e?: React.MouseEvent) => void;
  onSaveField?: (formId: string, fieldId: number, value: string) => Promise<void>;
  availableTasks: Task[];
  tasksLoading: boolean;
  validationResult?: FormValidationResult;
  labourOvertimeAllocations?: FormLabourOvertimeAllocationRow[];
}

interface Section {
  name: string;
  fields: Form['fields'];
  subsections: Record<string, Section>;
}

interface ActivityGroup {
  task?: string;
  sections: Record<string, Section>;
}

interface TaskGroup {
  id: string;
  task: string;
  taskId?: string;
  taskName?: string;
  sections: Record<string, Section>;
  fields: Form['fields'];
}

// Header mapping for overriding section titles
const sectionHeaderOverrides: Record<string, string> = {
  'Tasks 1': 'All Tasks',
  'CommentsPictures 3': 'Comments',
  'MaterialsUsed 1': 'Materials Details',
  'MaterialPickUp': 'Material Pick Up Location',
  'MaterialsUsed 2': 'Material',
  'Timecard and Job C 1': 'Labour Details',
  'VehicleUsage': 'Vehicle Usage',
  'WorkCompletedID': 'Work Completed ID',
  'TimeWorkedJobCost 1': 'Equipment and Subcontractor Costing',
  'EquipmentTimeWorkedJS': 'Costing',
  'EquipmentTimeWorkedJ': 'Subcontractor Selection',
  'WorkCompletedIdentif': 'Work Completed ID',
  'Activity 3': 'Accomplishments',
  'WinterRoad': 'Roads and Accomplishments',
  'WinterRoad 1': 'Road Details',
  'BlockRouteActivity': 'Block Route',
  'PerformingSecondaryA': 'Secondary Activity',
  'LandmarkSiteStruct 1': 'GPS Details',
  'LocationDetailsRes 1': 'Location Details'
};

// Define the order of sections
const sectionOrder: string[] = [
  'Labour Details',
  'Vehicle Usage',
  'Work Completed ID',
  'Activity 3',
  'Road & Accomplishment',
  'Equipment and Subcontractor Costing',
  'Comments'
];

// Add sections to hide
const hiddenSections = [
  'Material Details',
  'WorkCompletedID',
  'Work Completed ID',
  'Tasks 3',
  'Task3',
  'UsedMaterials',
];


/**
 * Stable empty default for `labourOvertimeAllocations`. An inline `= []` allocates a new array on
 * every render, and that array is a dependency of the validation effect below, which sets state
 * unconditionally — so omitting the prop spun the component in an infinite render loop.
 */
const NO_LABOUR_OVERTIME_ALLOCATIONS: FormLabourOvertimeAllocationRow[] = [];

const FormCompleteView: React.FC<FormCompleteViewProps> = ({ 
  form, 
  isExpanded, 
  onExpandToggle, 
  onSaveField,
  availableTasks,
  tasksLoading,
  validationResult: propValidationResult,
  labourOvertimeAllocations = NO_LABOUR_OVERTIME_ALLOCATIONS
}) => {
  const { hasPermission } = usePermissions();
  const canEditDailyLog = canEditDailyLogForForm(hasPermission, form);
  const canManageDailyLog = canManageDailyLogForForm(hasPermission, form);

  // Determine if fields should be editable
  const isEditable = (!form.status || form.status !== 'approved') 
    ? canEditDailyLog 
    : canManageDailyLog;

  // Check if form is approved - if so, disable all editing
  const isFormApproved = form.status === 'approved';
  const isFormDeclined = form.status === 'declined';
  const isFormNeedsRevisions = form.status === 'need revisions';
  const isFormReadOnly = isFormApproved || isFormDeclined || isFormNeedsRevisions;

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState<string>('');
  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [hasBeenWithdrawn, setHasBeenWithdrawn] = useState(false);
  const [validationResult, setValidationResult] = useState<FormValidationResult>(
    propValidationResult || {
      errors: [],
      warnings: [],
      hasErrors: false,
      hasWarnings: false
    }
  );
  
  /**
   * Reassign a Work Completed group to a different task.
   *
   * Shared by the RB task tabs, the RB task content header and the mechanic activity header: the
   * task id is written to both the `Task` and `TaskId` fields, whichever of them the group has.
   * The local field objects are mutated so the header reflects the new task before the parent
   * refetches.
   */
  const saveTaskAssignment = useCallback(
    async (
      taskIdField: Form['fields'][number] | undefined,
      taskField: Form['fields'][number] | undefined,
      newTaskId: string
    ) => {
      if (!onSaveField) return;

      // Task is required - the pickers pass allowClear={false}, so this is defensive only.
      if (!newTaskId || newTaskId.trim() === '') {
        alert('Task Name is required and cannot be cleared. Please select a task.');
        return;
      }

      if (taskIdField) {
        await onSaveField(form.id, taskIdField.id, newTaskId);
        taskIdField.value = newTaskId;
      }
      if (taskField) {
        await onSaveField(form.id, taskField.id, newTaskId);
        taskField.value = newTaskId;
      }

      setEditingTaskId(null);
    },
    [form.id, onSaveField]
  );

  /**
   * Reassign a mechanic Work Completed group to a different activity.
   *
   * The mechanic equivalent of {@link saveTaskAssignment}: a mechanic log has no Task, so the
   * activity is what identifies the work. Whichever of the group's activity fields exist are kept
   * in step - including `ActivityNumberTimber`, which the defects flow posts to Power Automate, so
   * a reassignment does not leave that payload pointing at the old activity.
   */
  const saveActivityAssignment = useCallback(
    async (fields: MechanicActivityFields, activity: Activity) => {
      if (!onSaveField) return;

      const number = String(activity.activityNumber ?? '').trim();
      if (!number) return;

      const writes: Array<[Form['fields'][number] | undefined, string]> = [
        [fields.activityNumberField, number],
        [fields.activityField, number],
        [fields.activityNameField, String(activity.activityName ?? '').trim()],
        [fields.activityTimberField, formatMechanicActivityNumberTimber(number)]
      ];

      for (const [field, value] of writes) {
        if (!field) continue;
        await onSaveField(form.id, field.id, value);
        field.value = value;
      }

      setEditingActivityGroupId(null);
    },
    [form.id, onSaveField]
  );

  // Keep these for backward compatibility
  const hasInvalidActivity = validationResult.errors.some(e => e.message.includes('999300'));
  const hasIneligibleTask = validationResult.errors.some(e => e.message.includes('status') && e.message.includes('cannot receive effort'));
  const hasMissingCHRISDetails = validationResult.hasWarnings;

  // Function to check if form has been withdrawn before
  const checkIfFormHasBeenWithdrawn = async (formId: string) => {
    try {
      const response = await fetch(`/api/forms/${formId}/history`);
      if (response.ok) {
        const history = await response.json();
        const hasWithdrawal = history.some((entry: any) => 
          entry.stage === 'withdraw_approval' || entry.stage === 'withdraw_revisions' || entry.stage === 'approval_withdrawn' || entry.stage === 'revisions_withdrawn' || entry.stage === 'decline_withdrawn'
        );
        setHasBeenWithdrawn(hasWithdrawal);
      }
    } catch (error) {
      console.error('Error checking form withdrawal history:', error);
      setHasBeenWithdrawn(false);
    }
  };

  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };
  /** Mechanic activity tab currently being reassigned to a different activity. */
  const [editingActivityGroupId, setEditingActivityGroupId] = useState<string | null>(null);
  /**
   * Activities the picker has shown, keyed by activity number, so a selection can be resolved back
   * to its full row. SearchableDropdown's `onChange` hands back only the value, and when
   * `fetchOptions` is supplied it lists ONLY what that fetch returned - the `options` prop is
   * ignored - so the fetch is the only place a selectable activity can come from. A ref rather
   * than state: the picker can fire `onChange` before a state update has re-rendered it.
   */
  const mechanicActivitiesByNumber = useRef<Map<string, Activity>>(new Map());
  /** Mechanic Work Completed tab (canonical id e.g. 1.0, 1.1). */
  const [mechanicActiveGroupId, setMechanicActiveGroupId] = useState<string>('');
  /** Per work tab: "Defects completed" panel open (default collapsed — header shows unit + defect ids). */
  const [mechanicDefectsCompletedExpanded, setMechanicDefectsCompletedExpanded] = useState<
    Record<string, boolean>
  >({});
  // Setup section: employees for Employee Name dropdown (timecard correction)
  const [setupEmployees, setSetupEmployees] = useState<Employee[]>([]);
  const [setupEmployeesLoading, setSetupEmployeesLoading] = useState(false);
  // Toast state
  const [toast, setToast] = useState<{
    show: boolean;
    message: string;
    type: ToastType;
  }>({
    show: false,
    message: '',
    type: 'info'
  });

  // Check if form has been withdrawn when component mounts
  useEffect(() => {
    checkIfFormHasBeenWithdrawn(form.id);
  }, [form.id]);

  // Load active employees for Employee Name dropdown when detailed view is expanded
  useEffect(() => {
    if (!isExpanded) return;
    let cancelled = false;
    setSetupEmployeesLoading(true);
    employeeService
      .getActiveEmployeesForSelection(form.homeForemanAreaId)
      .then((employees) => {
        if (!cancelled) setSetupEmployees(employees || []);
      })
      .catch(() => {
        if (!cancelled) setSetupEmployees([]);
      })
      .finally(() => {
        if (!cancelled) setSetupEmployeesLoading(false);
      });
    return () => { cancelled = true; };
  }, [isExpanded, form.id, form.homeForemanAreaId]);

  // Initialize tasks and validate form when tasks are available
  useEffect(() => {
    if (!tasksLoading && availableTasks.length > 0) {
      // Set initial filtered tasks
      setFilteredTasks(availableTasks);

      // Pre-process task groups and set initial active task
      const taskGroupsObj = groupFieldsByTask(form.fields);
      const taskGroupEntries = Object.entries(taskGroupsObj);
      if (!activeTaskId && taskGroupEntries.length > 0) {
        setActiveTaskId(taskGroupEntries[0][0]); // Use the group ID as the activeTaskId
      }

      // Validate form
      if (!propValidationResult) {
        const result = validateForm(form, availableTasks, hasBeenWithdrawn, labourOvertimeAllocations);
        setValidationResult(result);
      } else {
        setValidationResult(propValidationResult);
      }
    } else {
      setValidationResult({
        errors: [],
        warnings: [],
        hasErrors: false,
        hasWarnings: false
      });
    }
  }, [availableTasks, tasksLoading, form.fields, form.status, activeTaskId, hasBeenWithdrawn, propValidationResult, labourOvertimeAllocations]);

  // Mechanic Work Completed tabs: must run before any conditional return (Rules of Hooks).
  useEffect(() => {
    const hired = form.fields.some(
      (field) =>
        field.path.includes('EquipmentTimeWorkedJ') ||
        field.field === 'AmountBilled' ||
        field.path.includes('SubcontractorNameCom')
    );
    const mechanic = !hired && isMechanicDailyLog(form);
    if (!isExpanded || !mechanic) return;
    const ids = Object.keys(groupMechanicFieldsByWorkIndex(form)).sort(sortMechanicGroupKeys);
    if (ids.length === 0) return;
    setMechanicActiveGroupId((prev) => (prev && ids.includes(prev) ? prev : ids[0]));
  }, [isExpanded, form.id, form.fields]);

  // Add early return for loading state - don't render anything until tasks are loaded
  if (tasksLoading) {
    return (
      <Card>
        <Card.Header>
          <div className="tw-flex tw-justify-between tw-items-center">
            <div>
              <h5 className="tw-mb-0">{form.name}</h5>
              <small className="tw-text-content-muted tw-block">
                Loading task information...
              </small>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <Badge bg="secondary">
                {form.status || 'pending'}
              </Badge>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => onExpandToggle(form.id)}
              >
                <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="tw-flex tw-justify-center tw-items-center tw-py-5">
            <div className="tw-text-center">
              <Spinner className="tw-text-primary tw-mb-3">Loading...</Spinner>
              <p className="tw-text-content-muted tw-mb-0">Loading task information...</p>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  // Get the header information
  const employeeName = getFormEmployeeDisplayName(form);
  const defaultForemanArea = form.fields.find(f => f.field === 'ForemanArea')?.value || '';
  const foremanArea = defaultForemanArea;
  const date = form.fields.find(f => f.field === 'ActualStartTime')?.value || form.createdAt;
  // If the date is in YYYY-MM-DD format, display as-is to avoid timezone issues
  let formattedDate = date;
  if (typeof date === 'string' && /\d{4}-\d{2}-\d{2}$/.test(date)) {
    formattedDate = date;
  } else {
    try {
      formattedDate = new Date(date).toLocaleDateString();
    } catch {
      formattedDate = date;
    }
  }
  
  // Get SubcontractorNameCom for the title
  const subcontractorNameCom = form.fields.find(f => 
    f.path.includes('SubcontractorNameCom') || f.field === 'SubcontractorNameCom'
  )?.value;

  // Determine if it's a Hired Equipment form or Daily Log
  const isHiredEquipmentForm = form.fields.some(field => 
    field.path.includes('EquipmentTimeWorkedJ') || 
    field.field === 'AmountBilled' ||
    field.path.includes('SubcontractorNameCom')
  );
  const isMechanicForm = !isHiredEquipmentForm && isMechanicDailyLog(form);
  const formType = isHiredEquipmentForm
    ? 'Hired Equipment'
    : isMechanicForm
      ? 'Daily Log - Mechanic'
      : 'Daily Log - RB';
  const mechanicRows = isMechanicForm ? getMechanicWorkCompletedRows(form) : [];
  
  // Calculate total labour hours by summing up all task duration hours (excluding meal time)
  const totalLabourHours = form.fields
    .filter(f => f.field === 'TaskDurationHours' && f.path.includes('Timecard and Job C'))
    .reduce((sum, f) => {
      // Get the task number to check if it's a meal task
      const taskPath = f.path;
      const taskMatch = taskPath.match(/\.(\d+)\./);
      const taskId = taskMatch ? taskMatch[1] : '';
      
      // Get the task field to check if it's task 999001 (lunch)
      const taskGroupPath = taskPath.substring(0, taskPath.lastIndexOf('.'));
      const taskField = form.fields.find(tf => 
        tf.path.startsWith(taskGroupPath) && 
        tf.field === 'Task' && 
        tf.path.endsWith('.Task')
      );
      
      const taskNumber = taskField ? parseInt(taskField.value) : 0;
      
      // Exclude meal task (999001) from labour hours
      if (taskNumber === 999001) {
        return sum;
      }
      
      return sum + (parseFloat(f.value) || 0);
    }, 0);
  
  // Create title based on form type
  let headerTitle;
  if (isHiredEquipmentForm && subcontractorNameCom) {
    // For hired equipment: Subcontractor - Employee
    headerTitle = `${subcontractorNameCom} - ${employeeName} | Hours: ${totalLabourHours.toFixed(2)}`;
  } else if (subcontractorNameCom) {
    // For daily log with subcontractor: Employee - Subcontractor
    headerTitle = `${employeeName} - ${subcontractorNameCom} | Hours: ${totalLabourHours.toFixed(2)}`;
  } else {
    // No subcontractor: just employee name
    headerTitle = `${employeeName} | Hours: ${totalLabourHours.toFixed(2)}`;
  }

  // Create subtitle: "Form Type | Foreman Area | Date" (removed subcontractor since it's in title)
  let subtitle = formType;
  subtitle += ` | ${foremanArea} | ${formattedDate}`;

  // Get start time for the subtitle
  const startTime = form.fields.find(f => f.field === 'ActualStartTime')?.value;
  if (startTime) {
    try {
      const startTimeDate = new Date(startTime);
      if (!isNaN(startTimeDate.getTime())) {
        const hours = String(startTimeDate.getHours()).padStart(2, '0');
        const minutes = String(startTimeDate.getMinutes()).padStart(2, '0');
        const startTimeFormatted = `${hours}:${minutes}`;
        subtitle += ` | Start Time: ${startTimeFormatted}`;
      }
    } catch (error) {
      console.error('Error formatting start time:', error);
    }
  }

  // Helper function to format date/time values
  const formatDateTime = (dateTimeString: string): string => {
    if (!dateTimeString) return '-';
    
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return dateTimeString; // Return original if invalid date
      
      // Format as YYYY-MM-DD HH:MM
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateTimeString; // Return original on error
    }
  };

  // Helper function to determine which section a path belongs to
  const getSection = (path: string): 'Setup' | 'WorkCompleted' | 'CompletionSignoff' => {
    if (path.startsWith('WorkCompleted')) return 'WorkCompleted';
    if (path.startsWith('CompletionSignoff')) return 'CompletionSignoff';
    return 'Setup';
  };

  // Update the renderTable function to include pagination
  const renderTable = (fields: Form['fields'], indent?: boolean) => {
    const totalPages = Math.ceil(fields.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedFields = fields.slice(startIndex, endIndex);

    return (
      <div className={`${indent ? 'tw-ml-3' : ''}`}>
        <div className="tw-overflow-x-auto">
          <Table size="sm" striped hover className="tw-border tw-border-line">
            <colgroup>
              <col style={{ width: '35%' }} />
              <col style={{ width: '65%' }} />
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className="tw-align-middle tw-text-content-secondary tw-py-1">Field</th>
                <th scope="col" className="tw-align-middle tw-text-content-secondary tw-py-1">Value</th>
              </tr>
            </thead>
            <tbody className="tw-text-sm">
              {paginatedFields.map(field => {
                const isDateTimeField = 
                  field.field === 'End Date/Time' || 
                  field.field === 'Start Date/Time' || 
                  field.field === 'ActualStartTime' || 
                  field.field === 'ActualEndTime' ||
                  field.question === 'End Date/Time' || 
                  field.question === 'Start Date/Time';

                const isDurationField =
                  field.field === 'TotalTimeSpent' || field.dataType === 'Duration';

                const rawValue = field.value as unknown;
                const displayValue: string | undefined = isDateTimeField
                  ? formatDateTime(field.value)
                  : isDurationField
                    ? formatStoredDurationAnswerForDisplay(rawValue)
                    : field.value;
                
                return (
                  <tr key={field.id} className="tw-py-0">
                    <td className="tw-align-middle tw-text-content-muted tw-py-1">{field.question || field.field}</td>
                    <td className="tw-align-middle tw-py-1" title={displayValue || '-'}>
                      {isVehicleUnitFieldName(field.field) ? (
                        <VehicleUnitEditableField
                          fieldId={field.id}
                          fieldName={field.field}
                          value={displayValue}
                          onSave={async (fieldId, v, additionalUpdates) => {
                            await onSaveField?.(form.id, fieldId, v);
                            if (additionalUpdates) {
                              for (const update of additionalUpdates) {
                                await onSaveField?.(form.id, update.fieldId, update.value);
                              }
                            }
                          }}
                          form={form}
                          disabled={!onSaveField || isFormReadOnly}
                          homeForemanAreaId={form.homeForemanAreaId}
                        />
                      ) : (
                        <EditableField
                          fieldId={field.id}
                          fieldName={field.field}
                          value={displayValue}
                          onSave={async (fieldId, v, additionalUpdates) => {
                            await onSaveField?.(form.id, fieldId, v);
                            if (additionalUpdates) {
                              for (const update of additionalUpdates) {
                                await onSaveField?.(form.id, update.fieldId, update.value);
                              }
                            }
                          }}
                          form={form}
                          disabled={!onSaveField || isFormReadOnly}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <ServerPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={fields.length}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={handlePageSizeChange}
            maxVisiblePages={5}
            className="tw-mt-2"
          />
        </div>
      </div>
    );
  };

  const groupWorkCompletedFields = (fields: Form['fields']): TaskGroup[] => {
    const taskGroups: { [key: string]: TaskGroup } = {};
    
    fields.forEach(field => {
      if (!field.path.includes('WorkCompleted')) return;

      // Extract task group from the path using the same pattern as SummaryView
      const taskMatch = field.path.match(/WorkCompleted\.Tasks (\d+\.\d+)/);
      if (!taskMatch) return;
      
      const taskGroupId = taskMatch[1];
      
      if (!taskGroups[taskGroupId]) {
        taskGroups[taskGroupId] = {
          id: taskGroupId,
          task: '',
          sections: {},
          fields: []
        };
      }

      // Add field to the task group's fields array
      taskGroups[taskGroupId].fields.push(field);

      // Store TaskId field
      if (field.field === 'TaskId' || field.path.match(/\.TaskId$/)) {
        taskGroups[taskGroupId].taskId = field.value;
        
        // Debug logging for TaskId field processing
        console.log('Processing TaskId field:', {
          taskGroupId,
          field: field.field,
          path: field.path,
          value: field.value,
          parsedTaskId: parseInt(field.value),
          availableTasksCount: availableTasks.length,
          matchingTask: availableTasks.find(t => t.id === parseInt(field.value))
        });
        
        // If we have a taskId, try to find the task name from availableTasks
        const taskId = parseInt(field.value);
        const task = availableTasks.find(t => t.id === taskId);
        if (task && task.name) {
          taskGroups[taskGroupId].taskName = task.name;
        }
      }

      // Get task description if this is the Task field
      if (field.field === 'Task' && !field.path.includes('TaskId')) {
        taskGroups[taskGroupId].task = field.value;
        
        // Try to extract task ID if present
        if (!taskGroups[taskGroupId].taskId) {
          const numericValue = parseInt(field.value);
          if (!isNaN(numericValue)) {
            taskGroups[taskGroupId].taskId = numericValue.toString();
            // Look up the task name if we found an ID
            const task = availableTasks.find(t => t.id === numericValue);
            if (task && task.name) {
              taskGroups[taskGroupId].taskName = task.name;
            }
          }
        }
        return;
      }

      // Also check for WorkCompletedID field
      if (field.field === 'WorkCompletedID') {
        const workCompletedId = field.value;
        if (workCompletedId && !taskGroups[taskGroupId].taskId) {
          taskGroups[taskGroupId].taskId = workCompletedId;
          // Look up the task name if we found an ID
          const taskId = parseInt(workCompletedId);
          const task = availableTasks.find(t => t.id === taskId);
          if (task && task.name) {
            taskGroups[taskGroupId].taskName = task.name;
          }
        }
      }

      // Split path and remove empty segments
      const segments = field.path.split('.').filter(Boolean);
      let currentLevel = taskGroups[taskGroupId].sections;

      // Process all segments to build the nested structure
      for (let i = 0; i < segments.length; i++) {
        const currentSegment = segments[i];
        const nextSegment = segments[i + 1];
        
        // Skip if we're already processed this as an index
        if (/^\d+$/.test(currentSegment)) continue;

        // Check if this is a repeatable section (next segment is a number)
        const isRepeatable = nextSegment && /^\d+$/.test(nextSegment);
        
        if (isRepeatable) {
          // Create section with index (e.g., "Tasks 1.1", "Task 3")
          const sectionKey = `${currentSegment} ${nextSegment}`;
          if (!currentLevel[sectionKey]) {
            currentLevel[sectionKey] = {
              name: currentSegment,
              fields: [],
              subsections: {}
            };
          }
          currentLevel = currentLevel[sectionKey].subsections;
          i++; // Skip the index segment
        } else {
          // Regular section or field
          if (i === segments.length - 1) {
            // Last segment - this is a field, add to current level's fields
            const fieldsSection = currentLevel['fields'] || {
              name: 'fields',
              fields: [],
              subsections: {}
            };
            fieldsSection.fields.push(field);
            currentLevel['fields'] = fieldsSection;
          } else {
            // Not last segment - this is a section
            const sectionKey = currentSegment;
            if (!currentLevel[sectionKey]) {
              currentLevel[sectionKey] = {
                name: currentSegment,
                fields: [],
                subsections: {}
              };
            }
            currentLevel = currentLevel[sectionKey].subsections;
          }
        }
      }
    });

    // Convert to array and sort by task ID
    return Object.values(taskGroups).sort((a, b) => {
      // Split IDs into parts and convert to numbers for proper numeric sorting
      const [aMajor = 0, aMinor = 0] = a.id.split('.').map(Number);
      const [bMajor = 0, bMinor = 0] = b.id.split('.').map(Number);
      
      // First compare major version
      if (aMajor !== bMajor) return aMajor - bMajor;
      // If major versions are same, compare minor version
      return aMinor - bMinor;
    });
  };

  const renderSection = (section: Section, level: number = 0) => {
    // Get the overridden section name if it exists
    const displayName = sectionHeaderOverrides[section.name] || section.name;
    
    // Check if section should be hidden
    if (hiddenSections.includes(section.name) || hiddenSections.includes(displayName)) {
      return null;
    }
    
    // Hide any section with "Task 3" in its name
    if (displayName.includes('Task 3') || section.name.includes('Task 3')) {
      return null;
    }
    
    // Special case for MaterialsUsed sections
    const isMaterialsUsed = section.name === 'MaterialsUsed' || section.name === 'MaterialsUsed 1' || section.name === 'MaterialsUsed 2';
    const finalDisplayName = isMaterialsUsed ? 'Materials' : displayName;
    
    // Special case for MaterialsUsed - use the value of Material Used field if available
    let materialUsedValue = '';
    if (section.name === 'MaterialsUsed') {
      // Look for Material Used field in this section or its subsections
      const materialUsedField = section.fields.find(f => 
        f.field === 'Material Used' || 
        f.field === 'MaterialUsed' || 
        f.question === 'Material Used'
      );
      if (materialUsedField && materialUsedField.value) {
        materialUsedValue = ` - ${materialUsedField.value}`;
      } else {
        // Check in subsections
        for (const subsectionKey in section.subsections) {
          const subsection = section.subsections[subsectionKey];
          const field = subsection.fields.find(f => 
            f.field === 'Material Used' || 
            f.field === 'MaterialUsed' || 
            f.question === 'Material Used'
          );
          if (field && field.value) {
            materialUsedValue = ` - ${field.value}`;
            break;
          }
        }
      }
    }
    
    // Check if this is Task 3 section
    const isTask3Section = section.fields.some(field => 
      field.path === 'WorkCompleted.Tasks 1.0.Tasks.Task 3' ||
      field.path.startsWith('WorkCompleted.Tasks 1.0.Tasks.Task 3.')
    );

    // Determine if we should apply indentation
    const shouldIndent = level >= 3;
    const indentationClass = shouldIndent ? `tw-ml-3` : '';

    return (
      <div className={`section-level-${level} tw-mb-3`} style={{ display: isTask3Section ? 'none' : undefined }}>
        {section.name !== 'fields' && level >= 3 && (
          <div className={`tw-flex tw-items-center ${indentationClass} tw-mb-2`}>
            <h6 className="tw-mb-0 tw-text-primary">
              <i className="bi bi-folder2-open tw-mr-2"></i>
              {finalDisplayName}{materialUsedValue}
            </h6>
          </div>
        )}

        {section.fields.length > 0 && renderTable(section.fields, shouldIndent)}

        <div className={shouldIndent ? 'tw-border-l tw-border-line tw-ml-3 tw-pl-2' : ''}>
          {Object.entries(section.subsections)
            .sort(([keyA], [keyB]) => {
              // Get the display names for sorting
              const displayNameA = sectionHeaderOverrides[section.subsections[keyA].name] || section.subsections[keyA].name;
              const displayNameB = sectionHeaderOverrides[section.subsections[keyB].name] || section.subsections[keyB].name;
              
              // Check if both sections are in the ordered list
              const indexA = sectionOrder.indexOf(displayNameA);
              const indexB = sectionOrder.indexOf(displayNameB);
              
              // If both are in the ordered list, sort by that order
              if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
              }
              
              // If only one is in the ordered list, prioritize it
              if (indexA !== -1) return -1;
              if (indexB !== -1) return 1;
              
              // Default to alphabetical sorting
              return keyA.localeCompare(keyB);
            })
            .map(([key, subsection]) => (
              <div key={key}>
                {renderSection(subsection, level + 1)}
              </div>
            ))}
        </div>
      </div>
    );
  };

  const renderWorkCompletedSection = (fields: Form['fields']) => {
    const taskGroups = groupWorkCompletedFields(fields);
    
    // Set initial active task if not set
    if (!activeTaskId && taskGroups.length > 0) {
      setActiveTaskId(taskGroups[0].id);
    }

    const zeroLabourHourWarnings =
      validationResult.warnings.filter((w) => w.code === TASK_ZERO_LABOUR_HOURS_CODE);

    return (
      <div className="work-completed-section">
        {zeroLabourHourWarnings.length > 0 && (
          <Alert variant="warning" className="tw-mb-3 tw-flex tw-items-start tw-gap-2">
            <i className="bi bi-clock-history tw-shrink-0 tw-mt-1" aria-hidden />
            <div>
              <strong>
                {zeroLabourHourWarnings.length === 1
                  ? 'One task has 0 labour hours'
                  : 'Some tasks have 0 labour hours'}
              </strong>
              <span className="tw-block tw-text-sm tw-mt-1 tw-text-content-muted">
                Use the tabs below — tabs marked <Badge bg="warning" text="dark">0 hrs</Badge>{' '}
                need Task Duration updated if labour was performed.
              </span>
              <ul className="tw-mb-0 tw-mt-2 tw-text-sm tw-pl-3">
                {zeroLabourHourWarnings.map((w, idx) => (
                  <li key={`detail-zero-${w.taskGroupId}-${idx}`}>{w.message}</li>
                ))}
              </ul>
            </div>
          </Alert>
        )}
        {/* Task Tabs */}
        <ul className="tw-flex tw-flex-wrap tw-gap-1 tw-border-b tw-border-line tw-mb-2 tw-list-none tw-pl-0">
          {taskGroups.map(taskGroup => {
            // Prefer the Task field value as the ID, fallback to TaskId
            let currentTaskId: number | null = null;
            const taskField = taskGroup.fields.find(f => f.field === 'Task' && !f.path.includes('TaskId'));
            if (taskField && taskField.value) {
              const parsed = parseInt(taskField.value);
              if (!isNaN(parsed)) currentTaskId = parsed;
            }
            if (!currentTaskId && taskGroup.taskId) {
              const parsed = parseInt(taskGroup.taskId);
              if (!isNaN(parsed)) currentTaskId = parsed;
            }
            // Get task name and number from available tasks if we have a task ID
            let taskName = '';
            let taskNumber = '';
            if (currentTaskId) {
              const task = availableTasks.find(t => t.id === currentTaskId);
              taskName = task?.name || '';
              taskNumber = task?.taskNumber || task?.id?.toString() || '';
            }
            
            // Clean up task name to remove duplicate asset numbers
            const cleanTaskName = taskName.replace(/\(#\d+\)\s*\(#\d+\)/g, (match) => {
              // If we find duplicate asset numbers, keep only the first one
              const matches = match.match(/\(#\d+\)/g);
              return matches ? matches[0] : match;
            });
            
            let taskDescription = taskNumber
              ? `(#${taskNumber}) - ${cleanTaskName || taskGroup.taskName || taskGroup.task || `Task ${taskGroup.id}`}`
              : `${cleanTaskName || taskGroup.taskName || taskGroup.task || `Task ${taskGroup.id}`}`;
            
            // Check if this task group contains activity 999300
            const hasActivity999300 = form.fields.some(field => 
              field.path.includes(`WorkCompleted.Tasks ${taskGroup.id}`) && 
              (field.field === 'ActivityNumber' || field.question === 'Activity Number') &&
              field.value === '999300'
            );
            
            // Get the selected task to check if it has activity 999300
            let taskHas999300 = false;
            
            if (currentTaskId) {
              const task = availableTasks.find(t => t.id === currentTaskId);
              if (task) {
                if (task.activities) {
                  taskHas999300 = task.activities.some(activity => 
                    activity.activityNumber === '999300'
                  );
                }
              }
            }
            
            // Use either the detected activity or the task's activity
            const showActivity999300Warning = hasActivity999300 || taskHas999300;

            // Get errors and warnings for this task group
            const taskErrors = validationResult.errors.filter(e => e.taskGroupId === taskGroup.id);
            const taskWarnings = validationResult.warnings.filter(w => w.taskGroupId === taskGroup.id);
            const hasZeroLabourHourWarning = taskWarnings.some(
              (w) => w.code === TASK_ZERO_LABOUR_HOURS_CODE
            );
            
            return (
              <li key={taskGroup.id}>
                <button
                  className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 tw-bg-transparent ${activeTaskId === taskGroup.id ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
                  onClick={() => setActiveTaskId(taskGroup.id)}
                  title={
                    hasZeroLabourHourWarning ? `${taskDescription} — 0 labour hours` : taskDescription
                  }
                >
                  <span className="tw-flex tw-items-center tw-gap-2">
                    <span>{taskDescription}</span>
                    {hasZeroLabourHourWarning && (
                      <Badge
                        pill
                        bg="warning"
                        text="dark"
                        className="tw-font-semibold"
                        title="Task Duration (Hours) is 0 for this Work Completed row"
                      >
                        0 hrs
                      </Badge>
                    )}
                    {/* Error Badge */}
                    {taskErrors.length > 0 && (
                      <Badge
                        bg="danger"
                        text="white"
                        title={`${taskErrors.length} error${taskErrors.length > 1 ? 's' : ''}`}
                      >
                        <i className="bi bi-exclamation-triangle-fill"></i>
                        {taskErrors.length}
                      </Badge>
                    )}
                    {/* Warning Badge */}
                    {taskWarnings.length > 0 && (
                      <Badge
                        bg="warning"
                        text="dark"
                        title={`${taskWarnings.length} warning${taskWarnings.length > 1 ? 's' : ''}`}
                      >
                        <i className="bi bi-exclamation-triangle-fill"></i>
                        {taskWarnings.length}
                      </Badge>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {/* Task Content */}
        {taskGroups.map(taskGroup => {
          // Prefer the Task field value as the ID, fallback to TaskId
          let currentTaskId: number | null = null;
          const taskField = taskGroup.fields.find(f => f.field === 'Task' && !f.path.includes('TaskId'));
          if (taskField && taskField.value) {
            const parsed = parseInt(taskField.value);
            if (!isNaN(parsed)) currentTaskId = parsed;
          }
          if (!currentTaskId && taskGroup.taskId) {
            const parsed = parseInt(taskGroup.taskId);
            if (!isNaN(parsed)) currentTaskId = parsed;
          }
          // Get task name and number from available tasks if we have a task ID
          let taskName = '';
          let taskNumber = '';
          if (currentTaskId) {
            const task = availableTasks.find(t => t.id === currentTaskId);
            taskName = task?.name || '';
            taskNumber = task?.taskNumber || task?.id?.toString() || '';
          }
          
          // Clean up task name to remove duplicate asset numbers
          const cleanTaskName = taskName.replace(/\(#\d+\)\s*\(#\d+\)/g, (match) => {
            // If we find duplicate asset numbers, keep only the first one
            const matches = match.match(/\(#\d+\)/g);
            return matches ? matches[0] : match;
          });
          
          let taskDescription = taskNumber
            ? `(#${taskNumber}) - ${cleanTaskName || taskGroup.taskName || taskGroup.task || `Task ${taskGroup.id}`}`
            : `${cleanTaskName || taskGroup.taskName || taskGroup.task || `Task ${taskGroup.id}`}`;
          // Match server `submissionLabourLines.buildOrderedSubmissionLabourLines` exactly so
          // each Work Completed tab shows only its own line's breakdown (multiple tabs can point
          // at the same task; filtering by originalTaskId alone duplicates rows across tabs).
          const labourOtLineKey = `WorkCompleted.Tasks ${taskGroup.id}|task=${
            currentTaskId && currentTaskId > 0 ? currentTaskId : 'none'
          }`;
          const labourOtRowsForTask = labourOvertimeAllocations.filter(
            (row) =>
              Number(row.originalTaskId) === Number(currentTaskId) &&
              row.lineKey === labourOtLineKey &&
              (row.inputHours ?? 0) > 1e-4 &&
              row.id != null
          );
          
          // Check if this task group contains activity 999300
          const hasActivity999300 = form.fields.some(field => 
            field.path.includes(`WorkCompleted.Tasks ${taskGroup.id}`) && 
            (field.field === 'ActivityNumber' || field.question === 'Activity Number') &&
            field.value === '999300'
          );
          
          // Get the selected task to check if it has activity 999300
          let taskHas999300 = false;
          
          if (currentTaskId) {
            const task = availableTasks.find(t => t.id === currentTaskId);
            if (task) {
              if (task.activities) {
                taskHas999300 = task.activities.some(activity => 
                  activity.activityNumber === '999300'
                );
              }
            }
          }
          
          // Use either the detected activity or the task's activity
          const showActivity999300Warning = hasActivity999300 || taskHas999300;

          const handleTaskUpdate = async (taskId: string) => {
            const taskIdField = form.fields.find(field =>
              field.path.includes(`WorkCompleted.Tasks ${taskGroup.id}`) &&
              (field.field === 'TaskId' || field.path.match(/\.TaskId$/))
            );
            const taskField = form.fields.find(field =>
              field.path.includes(`WorkCompleted.Tasks ${taskGroup.id}`) &&
              field.field === 'Task' &&
              !field.path.includes('TaskId')
            );

            await saveTaskAssignment(taskIdField, taskField, taskId);
          };
          
          return (
            <div
              key={taskGroup.id}
              className={`task-content ${activeTaskId === taskGroup.id ? '' : 'tw-hidden'}`}
            >
              <Alert variant={showActivity999300Warning ? 'danger' : 'info'} className="task-header tw-py-2 tw-mb-2">
                <div className="tw-flex tw-justify-between tw-items-center tw-flex-wrap">
                  <div className="tw-flex tw-items-center tw-gap-2">
                    {editingTaskId === taskGroup.id ? (
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <div style={{ minWidth: '600px' }}>
                          <SearchableDropdown
                            options={filteredTasks}
                            value={currentTaskId?.toString() || ''}
                            onChange={(value) => handleTaskUpdate(value)}
                            formatOption={(task) => `${task.taskNumber || task.id} - ${task.name}`}
                            getValue={(task) => task.id.toString()}
                            placeholder="Select a task..."
                            // Async search: query server for top results while typing
                            fetchOptions={async (q: string) => {
                              const results = await taskService.searchActiveTasks(q, 10);
                              return results;
                            }}
                            minCharsForSearch={2}
                            disabled={isFormReadOnly}
                            allowClear={false}
                            required={true}
                          />
                        </div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setEditingTaskId(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <>
                        <h6 className="tw-mb-0">Task: {taskDescription}</h6>
                        {onSaveField && !isFormReadOnly && (
                          <Button
                            variant="link"
                            size="sm"
                            className="tw-p-0 tw-text-primary"
                            onClick={() => setEditingTaskId(taskGroup.id)}
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {showActivity999300Warning && (
                  <div className="tw-mt-2 tw-text-danger">
                    <small><strong>Warning:</strong> This task has activity number 999300. Please change to a different task before approval.</small>
                  </div>
                )}
              </Alert>
              {labourOtRowsForTask.length > 0 && (
                <Card className="tw-mb-3">
                  <Card.Body className="tw-py-2">
                    <h6 className="tw-mb-2">Labour Hours Breakdown</h6>
                    <p className="tw-text-sm tw-text-content-muted tw-mb-2">
                      Labour pay buckets for this task (straight + 1.5x + 2x = total).
                    </p>
                    {labourOtRowsForTask.map((row) => {
                      const straight = Number(row.labourStraightHours ?? 0);
                      const o15 = Number(row.labourOvertime15Hours ?? 0);
                      const o20 = Number(row.labourOvertime20Hours ?? 0);
                      const total = Number(row.inputHours ?? straight + o15 + o20);
                      return (
                        <div key={row.id} className="tw-border tw-border-line tw-rounded tw-p-2 tw-mb-2">
                          <div className="tw-text-sm">
                            Straight: {straight.toFixed(2)}h | OT 1.5: {o15.toFixed(2)}h | OT 2.0: {o20.toFixed(2)}h | Total: {total.toFixed(2)}h
                          </div>
                        </div>
                      );
                    })}
                  </Card.Body>
                </Card>
              )}
              {Object.entries(taskGroup.sections).map(([key, section]) => (
                <div key={key}>
                  {renderSection(section)}
                </div>
              ))}
              
              {/* Pictures for this specific task */}
              <FormPictures 
                formId={form.id} 
                taskGroupId={taskGroup.id}
                showOnlyWhenExpanded={false}
                isExpanded={true}
              />
            </div>
          );
        })}
      </div>
    );
  };

  // Filter fields by section
  const setupFields = form.fields.filter(
    (field) => getSection(field.path) === 'Setup' && field.field !== HIRED_EQUIPMENT_RESERVED_TICKET_FIELD,
  );
  const setupFieldsForDetailedView = isMechanicForm
    ? setupFields.filter((f) => {
        if (f.field === 'ForemanAreaCode') return false;
        if (
          f.field === 'ForemanAreaShop' &&
          setupFields.some((setupField) => setupField.field === 'ShopForemanArea')
        ) {
          return false;
        }
        if (f.field === 'RegularHours' || f.field === 'OvertimeHours') {
          const raw = String(f.value ?? '').trim();
          const v = parseFloat(raw);
          if (!raw || v === 0 || Number.isNaN(v)) return false;
        }
        return true;
      })
    : setupFields;

  const mechanicSetupFieldLabel = (f: Form['fields'][number]): string => {
    if (f.field === 'ForemanArea') return 'Home Foreman Area';
    if (f.field === 'ForemanAreaShop' || f.field === 'ShopForemanArea') return 'Shop Foreman Area';
    return f.question || f.field;
  };

  const isGenericShopSelectorValue = (value: string | undefined): boolean => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return false;
    return normalized === 'shop' || normalized.startsWith('material controls');
  };

  const resolveShopForemanAreaDisplayValue = (): string => {
    const shopForemanAreaValue =
      form.fields.find((f) => f.field === 'ShopForemanArea')?.value?.trim() || '';
    const legacyShopForemanAreaValue =
      form.fields.find((f) => f.field === 'ForemanAreaShop')?.value?.trim() || '';

    if (shopForemanAreaValue && !isGenericShopSelectorValue(shopForemanAreaValue)) {
      return shopForemanAreaValue;
    }
    if (legacyShopForemanAreaValue && !isGenericShopSelectorValue(legacyShopForemanAreaValue)) {
      return legacyShopForemanAreaValue;
    }
    return shopForemanAreaValue || legacyShopForemanAreaValue || '';
  };

  const workCompletedFields = form.fields.filter(field => getSection(field.path) === 'WorkCompleted');
  const signoffFields = form.fields.filter(field => getSection(field.path) === 'CompletionSignoff');

  const renderMechanicWorkCompletedSection = (): React.ReactNode => {
    const groups = groupMechanicFieldsByWorkIndex(form);
    const sortedIds = Object.keys(groups).sort(sortMechanicGroupKeys);
    if (sortedIds.length === 0) {
      return <p className="tw-text-content-muted tw-text-sm tw-mb-0">No work completed entries found.</p>;
    }
    const activeId =
      mechanicActiveGroupId && sortedIds.includes(mechanicActiveGroupId)
        ? mechanicActiveGroupId
        : sortedIds[0];
    const g = groups[activeId] || [];
    const classificationField = g.find(
      (f) => f.field === 'Classification' && f.path.includes('MechanicTasks')
    );
    const commentsField = g.find(
      (f) =>
        f.field === 'Comments' &&
        f.path.includes('MechanicTasks') &&
        (f.path.includes('.Tasks.Activity.') || f.path.includes('Tasks.Activity'))
    );
    const hoursStr = formatMechanicHoursWorkedFromGroupFields(g);
    const vehicleUnitLabel = getMechanicVehicleUnitForWorkCompletedGroup(form, activeId);
    const isVehicleWorkAndRepairs = isMechanicGroupVehicleWorkAndRepairs(form, activeId);
    const odometerLabel = isVehicleWorkAndRepairs
      ? getMechanicMileageForWorkCompletedGroup(form, activeId)
      : '';
    const vehicleUsage = getMechanicVehicleUsageFieldsForDisplay(g);
    const mechanicRow = mechanicRows.find((r) => r.groupId === activeId);
    const mealDurationMinutesRaw = g.find((f) => f.field === 'MealDurationMinutes')?.value;
    const mealDurationMinutes = Number.parseFloat(String(mealDurationMinutesRaw || '0'));
    const rawMealTime = g.find((f) => f.field === 'RawMealTime')?.value;
    const activityNameForDisplay = String(mechanicRow?.activity || '').toLowerCase();
    const activityNumberForDisplay = g.find(
      (f) =>
        f.field === 'Activity' &&
        f.path.includes('MechanicTasks') &&
        (f.path.includes('.Tasks.Activity.') || f.path.includes('Tasks.Activity'))
    )?.value;
    const isLunchMealsActivity =
      activityNameForDisplay.includes('lunch') ||
      activityNameForDisplay.includes('meal') ||
      String(activityNumberForDisplay || '').trim() === '999001';
    const mealDurationDisplay = (() => {
      if (mealDurationMinutes > 0) {
        const roundedMinutes = Math.round(mealDurationMinutes);
        return `${roundedMinutes} minute${roundedMinutes === 1 ? '' : 's'}`;
      }
      if (rawMealTime && String(rawMealTime).trim() !== '') {
        return String(rawMealTime);
      }
      return '-';
    })();
    const completedDefects =
      mechanicRow?.defects.filter((d) => isMechanicDefectMarkedCompleted(d.completed)) ?? [];
    const mechanicOtLineKey = buildMechanicSubmissionLabourLineKey(activeId);
    const labourOtRowsForMechanicGroup = labourOvertimeAllocations.filter(
      (row) =>
        row.lineKey === mechanicOtLineKey &&
        (row.inputHours ?? 0) > 1e-4 &&
        row.id != null
    );
    const unitLabelForDefects = mechanicRow?.unitLabel?.trim() || '';
    const defectsPanelExpanded = mechanicDefectsCompletedExpanded[activeId] === true;
    const hasUnit = Boolean(unitLabelForDefects);
    /** Match FieldTable chrome; value cells use fs-6 for slightly larger answers than `small` */
    const workSummaryColPct = hasUnit ? '33.33%' : '50%';

    // Task assignment for this activity — same affordance the RB Work Completed header has, so an
    // approver can clear the "task cannot receive effort / activity 999300" errors that
    // validateForm raises on mechanic logs too.
    const { taskField: mechanicTaskField, taskIdField: mechanicTaskIdField, currentTaskId: mechanicTaskId } =
      getMechanicTaskFieldsForGroup(form, activeId);
    /**
     * Most mechanic logs carry no task at all — TrueContext attributes the work by Activity, and
     * the effort builders hard-code `taskId: 0`. Render nothing rather than an empty "Task: —"
     * row that an approver reads as a broken control.
     */
    const mechanicHasTaskField = Boolean(mechanicTaskField || mechanicTaskIdField);
    const mechanicCanEditTask = Boolean(onSaveField && !isFormReadOnly && mechanicHasTaskField);
    const mechanicTask = mechanicTaskId
      ? availableTasks.find((t) => t.id === mechanicTaskId)
      : undefined;
    // Same duplicate "(#123) (#123)" scrub the RB header applies.
    const mechanicCleanTaskName = (mechanicTask?.name || '').replace(/\(#\d+\)\s*\(#\d+\)/g, (match) => {
      const matches = match.match(/\(#\d+\)/g);
      return matches ? matches[0] : match;
    });
    const mechanicTaskNumber = mechanicTask?.taskNumber || mechanicTask?.id?.toString() || '';
    const mechanicTaskDescription = (() => {
      if (!mechanicTaskId) return '—';
      if (mechanicTaskNumber) {
        return `(#${mechanicTaskNumber}) - ${mechanicCleanTaskName || `Task ${mechanicTaskId}`}`;
      }
      return mechanicCleanTaskName || `Task ${mechanicTaskId}`;
    })();
    const mechanicTaskHas999300 =
      mechanicTask?.activities?.some((activity) => activity.activityNumber === '999300') ?? false;

    // A mechanic log identifies its work by activity, not task - this is the mechanic equivalent
    // of the Road & Bridge task header.
    const mechanicActivityFields = getMechanicActivityFieldsForGroup(form, activeId);
    const mechanicCanEditActivity = Boolean(
      onSaveField &&
        !isFormReadOnly &&
        (mechanicActivityFields.activityNumberField || mechanicActivityFields.activityField)
    );
    const mechanicActivityDescription = (() => {
      const { currentActivityName: name, currentActivityNumber: num } = mechanicActivityFields;
      if (name && num) return name.includes(num) ? name : `${name} (${num})`;
      return name || num || '—';
    })();

    return (
      <div className="mechanic-work-completed-detailed">
        <ul className="tw-flex tw-flex-wrap tw-gap-1 tw-border-b tw-border-line tw-mb-0 tw-list-none tw-pl-0">
          {sortedIds.map((id) => (
            <li key={id}>
              <button
                type="button"
                className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 tw-bg-transparent ${activeId === id ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
                onClick={() => setMechanicActiveGroupId(id)}
              >
                {getMechanicActivityTabTitle(groups[id], id)}
              </button>
            </li>
          ))}
        </ul>
        <div className="tw-border tw-border-line tw-border-t-0 tw-rounded-b tw-p-3 tw-bg-surface">
          <Alert variant="info" className="activity-header tw-py-2 tw-mb-3">
            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-w-full">
              {editingActivityGroupId === activeId ? (
                <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-w-full">
                  <div className="tw-w-full sm:tw-w-auto sm:tw-min-w-[20rem] md:tw-min-w-[32rem] tw-grow">
                    <SearchableDropdown
                      options={[]}
                      value={mechanicActivityFields.currentActivityNumber}
                      onChange={(value) => {
                        const picked = mechanicActivitiesByNumber.current.get(value);
                        if (!picked) return;
                        void saveActivityAssignment(mechanicActivityFields, picked);
                      }}
                      formatOption={(a) => `${a.activityNumber} - ${a.activityName}`}
                      getValue={(a) => String(a.activityNumber)}
                      placeholder="Search activities..."
                      // Async search: query the server for top matches while typing. Remember what
                      // came back - `onChange` only gets the value, and this is the sole source of
                      // selectable rows.
                      fetchOptions={async (q: string) => {
                        const page = await activityService.getActivitiesPaginated({
                          search: q,
                          pageSize: 10
                        });
                        const results = page.data || [];
                        for (const activity of results) {
                          mechanicActivitiesByNumber.current.set(
                            String(activity.activityNumber),
                            activity
                          );
                        }
                        return results;
                      }}
                      minCharsForSearch={2}
                      disabled={isFormReadOnly}
                      allowClear={false}
                      required={true}
                    />
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setEditingActivityGroupId(null)}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <>
                  <h6 className="tw-mb-0">Activity: {mechanicActivityDescription}</h6>
                  {mechanicCanEditActivity && (
                    <Button
                      variant="link"
                      size="sm"
                      className="tw-p-0 tw-text-primary"
                      onClick={() => setEditingActivityGroupId(activeId)}
                      title="Change activity"
                      aria-label="Change activity"
                    >
                      <i className="bi bi-pencil"></i>
                    </Button>
                  )}
                </>
              )}
            </div>
          </Alert>
          {mechanicHasTaskField && (
          <Alert
            variant={mechanicTaskHas999300 ? 'danger' : 'info'}
            className="task-header tw-py-2 tw-mb-3"
          >
            <div className="tw-flex tw-justify-between tw-items-center tw-flex-wrap">
              <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-w-full">
                {editingTaskId === activeId ? (
                  <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-w-full">
                    <div className="tw-w-full sm:tw-w-auto sm:tw-min-w-[20rem] md:tw-min-w-[32rem] tw-grow">
                      <SearchableDropdown
                        options={filteredTasks}
                        value={mechanicTaskId?.toString() || ''}
                        onChange={(value) =>
                          saveTaskAssignment(mechanicTaskIdField, mechanicTaskField, value)
                        }
                        formatOption={(task) => `${task.taskNumber || task.id} - ${task.name}`}
                        getValue={(task) => task.id.toString()}
                        placeholder="Select a task..."
                        // Async search: query server for top results while typing
                        fetchOptions={async (q: string) => {
                          const results = await taskService.searchActiveTasks(q, 10);
                          return results;
                        }}
                        minCharsForSearch={2}
                        disabled={isFormReadOnly}
                        allowClear={false}
                        required={true}
                      />
                    </div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setEditingTaskId(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <>
                    <h6 className="tw-mb-0">Task: {mechanicTaskDescription}</h6>
                    {mechanicCanEditTask && (
                      <Button
                        variant="link"
                        size="sm"
                        className="tw-p-0 tw-text-primary"
                        onClick={() => setEditingTaskId(activeId)}
                        title="Change task"
                        aria-label="Change task"
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
            {mechanicTaskHas999300 && (
              <div className="tw-mt-2 tw-text-danger">
                <small><strong>Warning:</strong> This task has activity number 999300. Please change to a different task before approval.</small>
              </div>
            )}
          </Alert>
          )}
          <div className="tw-flex tw-flex-wrap -tw-mx-2 tw-text-sm tw-mb-3">
            <div className={`tw-px-2 tw-w-full ${isVehicleWorkAndRepairs ? 'md:tw-w-3/12' : 'md:tw-w-4/12'} tw-mb-2`}>
              <div className="tw-text-content-muted">Employee Classification</div>
              <div>{classificationField?.value?.trim() || '-'}</div>
            </div>
            <div className={`tw-px-2 tw-w-full ${isVehicleWorkAndRepairs ? 'md:tw-w-3/12' : 'md:tw-w-4/12'} tw-mb-2`}>
              <div className="tw-text-content-muted">{isLunchMealsActivity ? 'Meal Duration' : 'Hours worked'}</div>
              <div>{isLunchMealsActivity ? mealDurationDisplay : (hoursStr ? `${hoursStr} hours` : '-')}</div>
            </div>
            <div className={`tw-px-2 tw-w-full ${isVehicleWorkAndRepairs ? 'md:tw-w-3/12' : 'md:tw-w-4/12'} tw-mb-2`}>
              <div className="tw-text-content-muted">Vehicle unit</div>
              <div>{vehicleUnitLabel || '-'}</div>
            </div>
            {isVehicleWorkAndRepairs && (
              <div className="tw-px-2 tw-w-full md:tw-w-3/12 tw-mb-2">
                <div className="tw-text-content-muted">Odometer</div>
                <div>{odometerLabel ? formatMechanicMileageDisplay(odometerLabel) : '-'}</div>
              </div>
            )}
          </div>

          {labourOtRowsForMechanicGroup.length > 0 && (
            <Card className="tw-mb-3">
              <Card.Body className="tw-py-2">
                <h6 className="tw-mb-2">Labour Hours Breakdown</h6>
                <p className="tw-text-sm tw-text-content-muted tw-mb-2">
                  Labour pay buckets for this activity (straight + 1.5x + 2x = total).
                </p>
                {labourOtRowsForMechanicGroup.map((row) => {
                  const straight = Number(row.labourStraightHours ?? 0);
                  const o15 = Number(row.labourOvertime15Hours ?? 0);
                  const o20 = Number(row.labourOvertime20Hours ?? 0);
                  const total = Number(row.inputHours ?? straight + o15 + o20);
                  return (
                    <div key={row.id} className="tw-border tw-border-line tw-rounded tw-p-2 tw-mb-2">
                      <div className="tw-text-sm">
                        Straight: {straight.toFixed(2)}h | OT 1.5: {o15.toFixed(2)}h | OT 2.0:{' '}
                        {o20.toFixed(2)}h | Total: {total.toFixed(2)}h
                      </div>
                    </div>
                  );
                })}
              </Card.Body>
            </Card>
          )}
          {commentsField && (
            <div className="tw-overflow-x-auto tw-mb-3">
              <Table size="sm" striped hover className="tw-border tw-border-line tw-mb-0">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="tw-text-content-secondary tw-py-2 tw-text-sm tw-font-normal tw-border-b-0"
                    >
                      <i className="bi bi-chat-left-text tw-mr-2"></i>
                      Comments
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="tw-py-2 tw-pt-0 tw-border-t-0 mechanic-activity-comments-wrap tw-text-base">
                      <EditableField
                        fieldId={commentsField.id}
                        fieldName={commentsField.field}
                        value={commentsField.value}
                        onSave={async (fieldId, value, additionalUpdates) => {
                          await onSaveField?.(form.id, fieldId, value);
                          if (additionalUpdates) {
                            for (const update of additionalUpdates) {
                              await onSaveField?.(form.id, update.fieldId, update.value);
                            }
                          }
                        }}
                        form={form}
                        disabled={!onSaveField || isFormReadOnly}
                      />
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          )}

          {completedDefects.length > 0 && (
            <div className="tw-mb-3 tw-border tw-border-success/25 tw-rounded-lg tw-overflow-hidden">
              <button
                type="button"
                className="tw-w-full tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-left tw-py-2 tw-px-3 tw-border-0 tw-rounded-none"
                style={{ background: 'var(--bs-success-bg-subtle, rgba(25, 135, 84, 0.12))' }}
                onClick={() =>
                  setMechanicDefectsCompletedExpanded((prev) => ({
                    ...prev,
                    [activeId]: !prev[activeId]
                  }))
                }
                aria-expanded={defectsPanelExpanded}
              >
                <span className="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-success tw-text-sm">
                  <i
                    className={`bi ${defectsPanelExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}
                    aria-hidden
                  />
                  <i className="bi bi-check-circle-fill" aria-hidden />
                  <strong className="tw-text-black">Defects completed</strong>
                </span>
              </button>
              {defectsPanelExpanded && (
                <div className="tw-p-2 md:tw-p-3 tw-bg-surface tw-border-t tw-border-success/25">
                  <div className="tw-overflow-x-auto">
                    <Table size="sm" bordered className="tw-align-middle tw-mb-0 mechanic-defects-completed-table">
                      <thead>
                        <tr>
                          <th
                            scope="col"
                            className="tw-text-sm tw-text-content-secondary"
                            style={{ width: '10%', whiteSpace: 'nowrap' }}
                          >
                            Defect ID
                          </th>
                          <th scope="col" className="tw-text-sm tw-text-content-secondary" style={{ width: '18%' }}>
                            Category
                          </th>
                          <th scope="col" className="tw-text-sm tw-text-content-secondary">
                            Comments
                          </th>
                          <th
                            scope="col"
                            className="tw-text-sm tw-text-content-secondary"
                            style={{ width: '12%', whiteSpace: 'nowrap' }}
                          >
                            Completed
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {completedDefects.map((d, idx) => (
                          <tr key={`${activeId}-d-${idx}`}>
                            <td className="tw-font-medium tw-whitespace-nowrap tw-text-base">{d.defectId?.trim() || '—'}</td>
                            <td className="tw-break-words tw-text-base">{d.category?.trim() || '—'}</td>
                            <td className="tw-break-words tw-text-base" style={{ whiteSpace: 'pre-wrap' }}>
                              {d.comment?.trim() || '—'}
                            </td>
                            <td className="tw-whitespace-nowrap tw-text-base">
                              {isMechanicDefectMarkedCompleted(d.completed) ? (
                                <span className="tw-text-success tw-font-semibold">Yes</span>
                              ) : (
                                <span className="tw-text-content-muted">{d.completed?.trim() || '—'}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}

          {vehicleUsage && (
            <div className="tw-overflow-x-auto tw-mb-3">
              <Table size="sm" striped hover className="tw-border tw-border-line tw-mb-0">
                <thead>
                  <tr>
                    <th scope="col" className="tw-align-middle tw-text-content-secondary tw-py-1" colSpan={2}>
                      <i className="bi bi-truck-front tw-mr-2"></i>
                      Vehicle usage
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {vehicleUsage.vehicleUsed ? (
                    <tr>
                      <td className="tw-align-middle tw-text-content-muted tw-py-1 tw-text-sm" style={{ width: '35%' }}>
                        Vehicle used
                      </td>
                      <td className="tw-align-middle tw-py-1 tw-text-base">{vehicleUsage.vehicleUsed}</td>
                    </tr>
                  ) : null}
                  {vehicleUsage.durationLabel ? (
                    <tr>
                      <td className="tw-align-middle tw-text-content-muted tw-py-1 tw-text-sm">Duration of usage</td>
                      <td className="tw-align-middle tw-py-1 tw-text-base">{vehicleUsage.durationLabel}</td>
                    </tr>
                  ) : null}
                  {vehicleUsage.vehicleRate ? (
                    <tr>
                      <td className="tw-align-middle tw-text-content-muted tw-py-1 tw-text-sm">Vehicle rate</td>
                      <td className="tw-align-middle tw-py-1 tw-text-base">{vehicleUsage.vehicleRate}</td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
            </div>
          )}

          <div className="tw-mb-0">
            <h6 className="tw-text-primary tw-mb-2 tw-font-semibold">
              <i className="bi bi-image tw-mr-2"></i>
              Pictures
            </h6>
            <FormPictures
              formId={form.id}
              taskGroupId={activeId}
              showOnlyWhenExpanded={false}
              isExpanded={true}
            />
          </div>
        </div>
      </div>
    );
  };

  const handleHistoryComment = async (updatedForm: Form) => {
    // Update local form state with new history
    if (onSaveField) {
      // Refresh the form data if needed
      const updatedFields = updatedForm.fields;
      for (const field of updatedFields) {
        await onSaveField(form.id, field.id, field.value);
      }
    }
  };

  return (
    <>
      <Card className="tw-mb-3">
        {/* FormHeader removed for detailed view to avoid duplicate submission info bar */}
        {isExpanded && (
          <Card.Body>
            {/* Show read-only indicator for approved forms */}
            {isFormApproved && (
              <Alert variant="success" className="tw-mb-3">
                <i className="bi bi-check-circle tw-mr-2"></i>
                <strong>Form Approved</strong> - This form is read-only and cannot be edited.
              </Alert>
            )}

            {/* Show read-only indicator for declined forms */}
            {isFormDeclined && (
              <Alert variant="secondary" className="tw-mb-3">
                <i className="bi bi-x-circle tw-mr-2"></i>
                <strong>Form Declined</strong> - This form is read-only and cannot be edited.
              </Alert>
            )}

            {/* Show read-only indicator for forms needing revisions */}
            {isFormNeedsRevisions && (
              <Alert variant="warning" className="tw-mb-3">
                <i className="bi bi-exclamation-triangle tw-mr-2"></i>
                <strong>Form Needs Revisions</strong> - This form is read-only and cannot be edited while in revision status. The submitting employee will need to make the requested changes.
              </Alert>
            )}

            <SectionCard title="Setup">
              <FieldTable
                fields={setupFieldsForDetailedView}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={pageSize}
                onPageSizeChange={handlePageSizeChange}
                getFieldLabel={isMechanicForm ? (f) => mechanicSetupFieldLabel(f) : undefined}
                renderValue={(field) => {
                  if (field.field === 'ShopForemanArea' || field.field === 'ForemanAreaShop') {
                    return resolveShopForemanAreaDisplayValue() || field.value || '-';
                  }
                  // Employee Name: dropdown for timecard correction; selection populates Employee Number
                  const isEmployeeNameField = field.field === 'EmployeeName';
                  const canEditSetup = onSaveField && !isFormReadOnly;
                  if (isEmployeeNameField && canEditSetup) {
                    const displayNameFor = (emp: Employee) => `${(emp.name || '').trim()} ${(emp.lastName || '').trim()}`.trim();
                    if (setupEmployeesLoading) {
                      return <span className="tw-text-content-muted tw-text-sm">Loading employees...</span>;
                    }
                    return (
                      <SearchableDropdown<Employee>
                        options={setupEmployees}
                        value={field.value}
                        onChange={async (value) => {
                          const displayName = value;
                          await onSaveField?.(form.id, field.id, displayName);
                          const selected = setupEmployees.find((emp) => displayNameFor(emp) === displayName);
                          if (selected) {
                            const employeeNumberField = form.fields.find((f) => f.field === 'EmployeeNumber');
                            if (employeeNumberField) {
                              await onSaveField?.(form.id, employeeNumberField.id, selected.employeeNumber ?? '');
                            }
                          }
                        }}
                        formatOption={(emp) => displayNameFor(emp)}
                        getValue={(emp) => displayNameFor(emp)}
                        placeholder="Select employee..."
                        disabled={!onSaveField || isFormReadOnly}
                        allowClear={false}
                      />
                    );
                  }
                  const isStartOrEndDate =
                    field.field === 'ActualStartTime' ||
                    field.field === 'ActualEndTime' ||
                    field.field === 'Start Date/Time' ||
                    field.field === 'End Date/Time';
                  if (isStartOrEndDate) {
                    return (
                      <UIForm.Control
                        type="datetime-local"
                        size="sm"
                        value={getDateTimeLocalValue(field.value)}
                        onChange={(e) => onSaveField?.(form.id, field.id, formatDateTimeLocalForSave(e.target.value))}
                        onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                        disabled={!onSaveField || isFormReadOnly}
                      />
                    );
                  }
                  if (isVehicleUnitFieldName(field.field)) {
                    return (
                      <VehicleUnitEditableField
                        fieldId={field.id}
                        fieldName={field.field}
                        value={field.value}
                        onSave={async (fieldId, v, additionalUpdates) => {
                          await onSaveField?.(form.id, fieldId, v);
                          if (additionalUpdates) {
                            for (const update of additionalUpdates) {
                              await onSaveField?.(form.id, update.fieldId, update.value);
                            }
                          }
                        }}
                        form={form}
                        disabled={!onSaveField || isFormReadOnly}
                        homeForemanAreaId={form.homeForemanAreaId}
                      />
                    );
                  }
                  return (
                    <EditableField
                      fieldId={field.id}
                      fieldName={field.field}
                      value={field.value}
                      onSave={async (fieldId, value, additionalUpdates) => {
                        await onSaveField?.(form.id, fieldId, value);
                        if (additionalUpdates) {
                          for (const update of additionalUpdates) {
                            await onSaveField?.(form.id, update.fieldId, update.value);
                          }
                        }
                      }}
                      form={form}
                      disabled={!onSaveField || isFormReadOnly}
                    />
                  );
                }}
              />
            </SectionCard>
            <SectionCard title="Work Completed">
              {isMechanicForm && renderMechanicWorkCompletedSection()}

              {/* Errors and Warnings Section */}
              {(validationResult.hasErrors || validationResult.hasWarnings) && (
                <div className="tw-mb-4">
                  <h6 className="tw-mb-3">
                    <i className="bi bi-exclamation-triangle tw-mr-2"></i>
                    Errors and Warnings
                  </h6>

                  {/* Errors */}
                  {validationResult.hasErrors && (
                    <div className="tw-mb-3">
                      <Alert variant="danger" className="tw-mb-2">
                        <strong>
                          <i className="bi bi-exclamation-triangle-fill tw-mr-2"></i>
                          Errors ({validationResult.errors.length}):
                        </strong>
                      </Alert>
                      <ul className="tw-list-none tw-pl-0 tw-ml-4">
                        {validationResult.errors.map((error, index) => (
                          <li key={index} className="tw-mb-2">
                            <div className="tw-flex tw-items-start">
                              <i className="bi bi-x-circle-fill tw-text-danger tw-mr-2 tw-mt-1"></i>
                              <div>
                                <strong>Task {error.taskNumber ? `#${error.taskNumber}` : error.taskId || error.taskGroupId}</strong>
                                {error.taskName && ` - ${error.taskName}`}
                                <div className="tw-text-content-muted tw-text-sm">{error.message}</div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Warnings */}
                  {validationResult.hasWarnings && (
                    <div>
                      <Alert variant="warning" className="tw-mb-2">
                        <strong>
                          <i className="bi bi-exclamation-triangle-fill tw-mr-2"></i>
                          Warnings ({validationResult.warnings.length}):
                        </strong>
                      </Alert>
                      <ul className="tw-list-none tw-pl-0 tw-ml-4">
                        {validationResult.warnings.map((warning, index) => (
                          <li key={index} className="tw-mb-2">
                            <div className="tw-flex tw-items-start">
                              <i className="bi bi-exclamation-triangle-fill tw-text-warning-text tw-mr-2 tw-mt-1"></i>
                              <div>
                                <strong>{warning.taskGroupId === 'form' ? 'Submission' : `Task ${warning.taskNumber ? `#${warning.taskNumber}` : warning.taskId || warning.taskGroupId}`}</strong>
                                {warning.taskName && ` - ${warning.taskName}`}
                                <div className="tw-text-content-muted tw-text-sm">{warning.message}</div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              {!isMechanicForm && renderWorkCompletedSection(workCompletedFields)}
            </SectionCard>
            <SectionCard title="Completion & Signoff">
              <FieldTable
                fields={signoffFields}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={pageSize}
                onPageSizeChange={handlePageSizeChange}
                renderValue={(field) => {
                  const isStartOrEndDate =
                    field.field === 'ActualStartTime' ||
                    field.field === 'ActualEndTime' ||
                    field.field === 'Start Date/Time' ||
                    field.field === 'End Date/Time';
                  if (isStartOrEndDate) {
                    return (
                      <UIForm.Control
                        type="datetime-local"
                        size="sm"
                        value={getDateTimeLocalValue(field.value)}
                        onChange={(e) => onSaveField?.(form.id, field.id, formatDateTimeLocalForSave(e.target.value))}
                        onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                        disabled={!onSaveField || isFormReadOnly}
                      />
                    );
                  }
                  if (isVehicleUnitFieldName(field.field)) {
                    return (
                      <VehicleUnitEditableField
                        fieldId={field.id}
                        fieldName={field.field}
                        value={field.value}
                        onSave={async (fieldId, v, additionalUpdates) => {
                          await onSaveField?.(form.id, fieldId, v);
                          if (additionalUpdates) {
                            for (const update of additionalUpdates) {
                              await onSaveField?.(form.id, update.fieldId, update.value);
                            }
                          }
                        }}
                        form={form}
                        disabled={!onSaveField || isFormReadOnly}
                        homeForemanAreaId={form.homeForemanAreaId}
                      />
                    );
                  }
                  return (
                    <EditableField
                      fieldId={field.id}
                      fieldName={field.field}
                      value={field.value}
                      onSave={async (fieldId, value, additionalUpdates) => {
                        await onSaveField?.(form.id, fieldId, value);
                        if (additionalUpdates) {
                          for (const update of additionalUpdates) {
                            await onSaveField?.(form.id, update.fieldId, update.value);
                          }
                        }
                      }}
                      form={form}
                      disabled={!onSaveField || isFormReadOnly}
                    />
                  );
                }}
              />
            </SectionCard>
          </Card.Body>
        )}
      </Card>
      <FormHistoryModal
        visible={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        form={form}
        onAddComment={handleHistoryComment}
      />
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(prev => ({ ...prev, show: false }))}
      />
    </>
  );
};

export default FormCompleteView; 